/* eslint-disable max-len */
/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { createSlice } from '@reduxjs/toolkit';
import { ThunkStatus } from '../../enums/ThunkStatus';
import { patchLockSubmissionThunk, patchUnlockSubmissionThunk } from './submission.thunk';
import { getValidationsThunk } from './submission.thunk.validations';
import {
  putUserSignatureThunk,
  postSendToSignAndPackThunk,
  deleteUserSignatureThunk,
} from './submission.thunk.signing';
import { submissionSliceName } from './submission.selectors';
import { SubmissionStatus } from '../../../models/SubmissionStatus';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';

/* Initial state */
const initialState = {
  status: ThunkStatus.IDLE,
  data: undefined,
  // duplicated state because 'data' will be altered by different thunks
  // which might result in losing the locked submission id (e.g. postSendToSignAndPackThunk.fulfilled)
  lockedSubmissionId: undefined,
  loadingAttachmentsCount: 0,
  uiSchema: undefined,
  workflowValidations: {
    isValid: true,
    extraErrors: {},
    sectionValidations: {},
  },
  validationErrors: [],
  isRightMenuOpen: false,
};

// /**
//  * support validations for submission configurations for pending
//  * @param action
//  * @returns {boolean}
//  */
// const getSubmissionValidationPendingMatcher = action =>
//   [getValidationsThunk.pending].includes(action.type) && action.payload?.uiSchema?.[uiSchemaProperties.DIALOG_ID];

/**
 * support validations for submission configurations for fullfilled
 * @param action
 * @returns {boolean}
 */
const getSubmissionValidationFulfilledMatcher = (action) => {
  // Convert the thunk action type to its string representation for comparison
  const expectedFulfilledTypeString = getValidationsThunk.fulfilled.toString();

  // Check if the action type matches the expected fulfilled type
  // and if the uiSchema with the specific DIALOG_ID does not exist in the payload
  return action.type === expectedFulfilledTypeString && !action.payload?.uiSchema?.[uiSchemaProperties.DIALOG_ID];
};

/**
 * support validations for submission configurations for rejected
 * @param action
 * @returns {boolean}
 */
const getSubmissionValidationRejectedMatcher = (action) => {
  // Convert the thunk action type to its string representation for comparison
  const expectedRejectedTypeString = getValidationsThunk.rejected.toString();

  // Check if the action type matches the expected rejected type
  // and if the uiSchema with the specific DIALOG_ID does not exist in the payload
  return action.type === expectedRejectedTypeString && !action.payload?.uiSchema?.[uiSchemaProperties.DIALOG_ID];
};

/* Slice */
export const submissionSlice = createSlice({
  name: submissionSliceName,
  initialState,
  reducers: {
    toggleRightMenu(state) {
      state.isRightMenuOpen = !state.isRightMenuOpen;
    },
    setSubmission(state, action) {
      state.data = action.payload;
    },
    increaseLoadingAttachmentsCount(state) {
      state.loadingAttachmentsCount += 1;
    },
    decreaseLoadingAttachmentsCount(state) {
      state.loadingAttachmentsCount -= 1;
    },
  },
  extraReducers(builder) {
    builder
      // extra reducers for locking / unlocking
      .addCase(patchLockSubmissionThunk.fulfilled, (state, action) => {
        state.status = ThunkStatus.SUCCEEDED;
        state.data = action.payload;
        state.lockedSubmissionId = action.payload?.id;
      })
      .addCase(patchLockSubmissionThunk.rejected, (state) => {
        state.status = ThunkStatus.FAILED;
      })
      .addCase(patchUnlockSubmissionThunk.fulfilled, (state) => {
        state.status = initialState.status;
        state.data = initialState.data;
        state.uiSchema = initialState.uiSchema;
        state.validationErrors = initialState.validationErrors;
        state.isValid = initialState.isValid;
        state.lockedSubmissionId = initialState.lockedSubmissionId;
      })
      .addCase(patchUnlockSubmissionThunk.rejected, (state) => {
        state.status = ThunkStatus.FAILED;
        state.data = initialState.data;
        state.lockedSubmissionId = initialState.lockedSubmissionId;
      })
      // extra reducers for managing signature data
      .addCase(putUserSignatureThunk.fulfilled, (state, action) => {
        state.data = action.payload;
      })
      .addCase(deleteUserSignatureThunk.fulfilled, (state, action) => {
        state.data = action.payload;
      })

      // extra reducers for sending to sign and pack
      .addCase(postSendToSignAndPackThunk.fulfilled, (state) => {
        state.status = initialState.status;
        state.data = initialState.data;
        state.uiSchema = initialState.uiSchema;
        state.validationErrors = initialState.validationErrors;
        state.isValid = initialState.isValid;
      })
      // extra reducers for validations
      .addMatcher(getSubmissionValidationFulfilledMatcher, (state, action) => {
        const submission = action.payload?.submission;
        const saveOnValidateStatuses = [SubmissionStatus.DRAFT.id, SubmissionStatus.READY_TO_PAY.id, SubmissionStatus.READY_TO_SIGN.id];
        if (saveOnValidateStatuses.includes(submission.status)) {
          state.data = action.payload?.submission;
        }
        state.uiSchema = action.payload?.uiSchema;
        state.validationErrors = action.payload?.validationErrors ?? initialState.validationErrors;
        state.workflowValidations = action.payload?.workflowValidations;
        state.isValid = !!action.payload?.workflowValidations?.isValid;
      })
      .addMatcher(getSubmissionValidationRejectedMatcher, (state) => {
        state.isValid = false;
      });
  },
});

/* Actions */
export const {
  toggleRightMenu,
  setSubmission,
  increaseLoadingAttachmentsCount,
  decreaseLoadingAttachmentsCount,
} = submissionSlice.actions;
